import React, { useEffect } from 'react';
import {
    FileSignature, ShieldCheck, CreditCard, ClipboardList, Video, LayoutGrid,
    Clock, CheckCircle2, Zap, MoveRight, Download, ExternalLink,
} from 'lucide-react';

/*
 * Debmita Dutta onboarding hub — Founder-Led Content System (SLA signed 2026-06-15).
 * Built from .claude/skills/onboard-client/onboarding-template.jsx.
 * Adapted for her signed scope: NO AI clone, authentic recording, 4-level QC,
 * she self-posts Instagram + LinkedIn, we post YouTube Shorts, ~2-week onboarding.
 */

// --- Per-client fill-ins ---
const SLUG = 'debmita';
const SHARE_EMAIL = 'debmita.dutta@gmail.com';
const NOTION_CONTENT_OS = 'https://app.notion.com/p/3883ac2a5309817a8169f69980a8890b';

// --- Shared across every client (rarely change) ---
const QUESTIONNAIRE_TEMPLATE_URL = 'https://docs.google.com/document/d/1kcVG8AyrX3Rp93jE06ysFhO0Ub72nAF03Q4XIpoSkdM/edit';

const data = {
    client: 'Debmita',
    brandLabel: 'Debmita Dutta',
    pageTitle: 'Onboarding | Debmita Dutta via SocialSEO',
    badge: 'Signed & Starting',
    headline: 'Welcome aboard, Debmita.',

    greeting: 'Hi Debmita,',
    intro: [
        'Lovely to have you in. This page is your single home base for getting started. Everything you need in the first few days is laid out below as a short checklist.',
        'A quick note on how it runs. The first two weeks are onboarding: we set up your Notion content engine, lock your pillars and recording rhythm, and mine your existing library for hooks. Your 3-month clock and your billing do not start on signing, they start the day your first content goes live. That prep runway is on us.',
        'Work down the steps. Once the first invoice is cleared, we move straight into prep and production.',
    ],

    steps: [
        {
            icon: FileSignature,
            title: 'Your signed agreement',
            done: true,
            desc: 'Signed and countersigned, this one is already done. Here is your copy for your records. We are fully aligned on the scope, the four-level quality control, and that everything stays your real voice and face, with no AI clone at any point.',
            links: [
                { label: 'Service Agreement (signed)', href: `/assets/${SLUG}/agreement.pdf`, icon: Download },
            ],
        },
        {
            icon: CreditCard,
            title: 'Clear your first invoice',
            desc: 'The first month is ₹1,00,000 + 18% GST, a total of ₹1,18,000, payable by bank transfer. Full account details are printed on the invoice. This first payment commences onboarding and the first production cycle.',
            links: [
                { label: 'Download Invoice (bank details inside)', href: `/assets/${SLUG}/invoice.pdf`, icon: Download },
            ],
        },
        {
            icon: ClipboardList,
            title: 'Fill your Founder Questionnaire',
            desc: 'This is your own copy, ready to fill. It is how we lock your positioning, voice, story, and content pillars. Open it, answer the questions, and you are done. It takes about 20 minutes, and the sharper your answers, the sharper the content.',
            links: [
                { label: 'Open your Questionnaire', href: QUESTIONNAIRE_TEMPLATE_URL, icon: ExternalLink },
            ],
        },
        {
            icon: Video,
            title: 'Share your content library and recording rhythm',
            desc: `Two things from your side, and then you only ever have to speak to camera. Share access to your existing long-form, Facebook Lives and podcast appearances so we can mine hooks and angles, and pick a recurring recording slot. We send ready-to-record prompts, you record on your own device, and we do the rest. Send your library to ${SHARE_EMAIL}.`,
            links: [],
            tips: [
                'Existing content: Facebook Lives, podcast appearances, YouTube long-form, and any prior recordings we can repurpose into clips.',
                'Recording slot: a recurring window where you batch-record the talking-head reels from our prompts.',
                'Your real voice and face only. No AI clone, no synthetic likeness, ever.',
                `Share everything to ${SHARE_EMAIL}.`,
            ],
        },
        {
            icon: LayoutGrid,
            title: 'Open your Content OS',
            desc: 'Your Notion workspace is where the whole engine lives: the ideas and scripts pipeline, the posting calendar, your approvals, and weekly performance. This is where you give inputs and approve every reel before it goes out.',
            links: [
                { label: 'Open your Content OS', href: NOTION_CONTENT_OS, icon: ExternalLink },
            ],
        },
    ],

    timelineTitle: 'What the engagement looks like',
    phases: [
        {
            title: 'Onboarding & Setup',
            window: 'Weeks 1–2',
            items: [
                'First invoice cleared, questionnaire in, content library shared.',
                'We deploy your Notion Content OS and lock your pillars and recording rhythm.',
                'We mine your existing library for hooks and prep your first batch of scripts.',
            ],
        },
        {
            title: 'Launch',
            window: 'Go-live day',
            items: [
                'Your first reels go live. Your 3-month clock and billing start here, not before.',
                'Hooks calibrated for your audience across Instagram, YouTube Shorts and LinkedIn.',
            ],
        },
        {
            title: 'Month 1 Production',
            window: 'Weeks 1–4',
            items: [
                '30 reels a month ship: 20 talking-head from your prompts, plus 10 clips from your library.',
                'Every reel clears the four-level quality control before it goes out.',
                'You publish Instagram and LinkedIn, we handle YouTube Shorts, and we review the numbers together weekly.',
            ],
        },
        {
            title: 'Funnel & Momentum',
            window: 'Month 2',
            items: [
                'The DM nurture funnel routes enquiries into your booking flow.',
                'Hook discipline tightens and the back catalogue starts compounding.',
                'Course applications tracked as the leading metric.',
            ],
        },
        {
            title: 'Compound & Review',
            window: 'Month 3',
            items: [
                'The full system is optimized and the library compounds.',
                'Monthly direction review, and the renewal conversation at a rate to be agreed.',
            ],
        },
    ],
    timelineNote:
        'Onboarding is the heavy-lift fortnight. Once your Notion engine is live and the recording rhythm is set, production runs like clockwork.',

    closing: {
        line1: 'Any questions on the documents, the questionnaire, or the recording setup, just message me.',
        line2: 'Clear the checklist and we get straight into prep!',
    },
};

export default function DebmitaOnboarding() {
    useEffect(() => {
        document.title = data.pageTitle;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">{data.brandLabel} Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">

                {/* Intro */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> {data.badge}
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        {data.headline}
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>{data.greeting}</strong></p>
                        {data.intro.map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                </section>

                {/* Onboarding checklist */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-400 w-6 h-6" /> Your First Steps
                    </h2>

                    <div className="space-y-5">
                        {data.steps
                            .filter((step) => !step.links.some((l) => l.href.includes('_PENDING')))
                            .map((step, i) => {
                                const StepIcon = step.icon;
                                return (
                                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 shadow-xl hover:border-indigo-500/40 transition-colors">
                                        <div className="flex items-start gap-4">
                                            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-bold relative ${step.done ? 'bg-emerald-500/15 text-emerald-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                                                <StepIcon className="w-5 h-5" />
                                                <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0B0E14] ${step.done ? 'bg-emerald-500' : 'bg-indigo-500'}`}>{step.done ? '✓' : i + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">{step.title}{step.done && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">Done</span>}</h3>
                                                <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.desc}</p>

                                                {step.tips && (
                                                    <ul className="mb-4 space-y-2">
                                                        {step.tips.map((tip, t) => (
                                                            <li key={t} className="flex gap-2.5 text-slate-300 text-sm">
                                                                <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                <div className="flex flex-wrap gap-3">
                                                    {step.links.map((link, l) => {
                                                        const LinkIcon = link.icon || ExternalLink;
                                                        return (
                                                            <a
                                                                key={l}
                                                                href={link.href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-sm hover:bg-indigo-500/20 hover:border-indigo-400 hover:text-white transition-colors"
                                                            >
                                                                <LinkIcon className="w-4 h-4" /> {link.label} <MoveRight className="w-4 h-4" />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> {data.timelineTitle}
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        {data.phases.map((phase, i) => (
                            <div key={i} className="relative pl-8 md:pl-10">
                                <div className={`absolute -left-[14px] top-1 w-7 h-7 rounded-full flex items-center justify-center ${
                                    i === 0
                                        ? 'bg-indigo-500 border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                        : 'bg-[#1A1D24] border border-white/20'
                                }`}>
                                    <span className={`text-xs font-bold ${i === 0 ? 'text-white' : 'text-white/60'}`}>{i + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-1 text-white">{phase.title}</h3>
                                <p className={`text-sm font-semibold mb-3 uppercase tracking-wider ${i === 0 ? 'text-indigo-400' : 'text-indigo-400/70'}`}>
                                    {phase.window}
                                </p>
                                <ul className={`space-y-3 ${i === 0 ? 'text-slate-300' : 'text-slate-300/80'}`}>
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="flex gap-3">
                                            <Zap className="w-5 h-5 text-indigo-400 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {data.timelineNote && (
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                            <strong className="text-indigo-400">Note:</strong> {data.timelineNote}
                        </div>
                    )}
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                        <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">{data.closing.line1}</p>
                        <p className="text-lg font-bold text-white">{data.closing.line2}</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
